import { call, fork, put, takeEvery, select, take, race } from 'redux-saga/effects';
import { normalize } from 'normalizr';
import { startSubmit, stopSubmit } from 'redux-form';
import { show as showModal, hide as hideModal } from 'redux-modal';

import * as schemas from 'store/common/schemas';
import * as entityActions from 'store/entities/actions';
import * as actionTypes from './action-types';
import * as actions from './actions';
import * as wizardActions from 'store/features/wizard/actions';
import * as api from 'api/config/work-experience';
import callApi from 'store/api/saga';
import * as authSelectors from 'store/features/auth/selectors';
import { messageTypes } from 'store/features/common/flash/builder';
import { addMessage } from 'store/features/common/flash/actions';
import messages from 'ui/work-experience/messages';
import { MODAL_NAME as DELETE_WORK_EXPERIENCE_ENTRY_CONFIRMATION_MODAL_NAME } from 'ui/work-experience/delete/confirmation-dialog';

function* addWorkExperienceEntry({ payload: values, meta }) {
  const { form, modal, wizard } = meta;
  yield put(actions.addWorkExperienceEntry.start(values));
  if (form) yield put(startSubmit(form));

  try {
    const response = yield call(callApi, api.addWorkExperienceEntry(values));
    yield put(actions.addWorkExperienceEntry.success(response));
    if (form) yield put(stopSubmit(form));
    if (modal) yield put(hideModal(modal));

    const currentUserId = yield select(authSelectors.selectCurrentUserId);
    const schema = { work: schemas.workExperienceEntry };
    if (response.work) {
      response.work.userId = currentUserId;
    }
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));
    if (!wizard) {
      yield put(
        addMessage({
          id: 'work-experience-entry-operation-status',
          kind: messageTypes.success,
          content: messages.entryAdded,
        })
      );
    }
    if (wizard) {
      const { name, apiConfig } = wizard;
      yield put(wizardActions.completeStep.request({ name, apiConfig }));
    }
  } catch (error) {
    yield put(actions.addWorkExperienceEntry.failure(error));
    if (form) yield put(stopSubmit(form, error.formErrors));
  }
}

function* watchAddWorkExperienceEntry() {
  yield takeEvery(actionTypes.ADD_WORK_EXPERIENCE_ENTRY.REQUEST, addWorkExperienceEntry);
}

function* updateWorkExperienceEntry({ payload: values, meta }) {
  const { form, modal } = meta;
  yield put(actions.updateWorkExperienceEntry.start(values));
  if (form) yield put(startSubmit(form));

  try {
    const response = yield call(callApi, api.updateWorkExperienceEntry(values));
    yield put(actions.updateWorkExperienceEntry.success(response));
    if (form) yield put(stopSubmit(form));
    if (modal) yield put(hideModal(modal));

    const schema = { work: schemas.workExperienceEntry };
    if (response.work && !response.work.userId) {
      const currentUserId = yield select(authSelectors.selectCurrentUserId);
      response.work.userId = currentUserId;
    }
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities, { [schema.work.key]: { deep: false } }));
    yield put(
      addMessage({
        id: 'work-experience-entry-operation-status',
        kind: messageTypes.success,
        content: messages.entryUpdated,
      })
    );
  } catch (error) {
    yield put(actions.updateWorkExperienceEntry.failure(error));
    if (form) yield put(stopSubmit(form, error.formErrors));
  }
}

function* watchUpdateWorkExperienceEntry() {
  yield takeEvery(actionTypes.UPDATE_WORK_EXPERIENCE_ENTRY.REQUEST, updateWorkExperienceEntry);
}

function* askDeleteWorkExperienceEntryConfirmation({ id }) {
  const modalName = DELETE_WORK_EXPERIENCE_ENTRY_CONFIRMATION_MODAL_NAME(id);
  yield put(showModal(modalName));
  const { yes } = yield race({
    yes: take(
      action =>
        action.type === actionTypes.DELETE_WORK_EXPERIENCE_ENTRY.CONFIRM &&
        action.payload &&
        action.payload.id === id
    ),
    no: take(
      action =>
        action.type === actionTypes.DELETE_WORK_EXPERIENCE_ENTRY.CANCEL &&
        action.payload &&
        action.payload.id === id
    ),
  });
  yield put(hideModal(modalName));
  return !!yes;
}

function* deleteWorkExperienceEntry({ payload: values, meta }) {
  const { id } = values;
  const confirmed = yield call(askDeleteWorkExperienceEntryConfirmation, { id });
  if (!confirmed) {
    return;
  }

  const { modal } = meta;
  yield put(actions.deleteWorkExperienceEntry.start(values));

  try {
    yield call(callApi, api.deleteWorkExperienceEntry(values));
    yield put(actions.deleteWorkExperienceEntry.success(values));
    if (modal) yield put(hideModal(modal));
    yield put(
      addMessage({
        id: 'work-experience-entry-operation-status',
        kind: messageTypes.success,
        content: messages.entryDeleted,
      })
    );
  } catch (error) {
    yield put(actions.deleteWorkExperienceEntry.failure(error));
  }
}

function* watchDeleteWorkExperienceEntry() {
  yield takeEvery(actionTypes.DELETE_WORK_EXPERIENCE_ENTRY.REQUEST, deleteWorkExperienceEntry);
}

export default function* workExperience() {
  yield fork(watchAddWorkExperienceEntry);
  yield fork(watchUpdateWorkExperienceEntry);
  yield fork(watchDeleteWorkExperienceEntry);
}
